import React, { useEffect, useState } from 'react';
import { Accident, ReplacementVehicleRequest } from '../types';
import { ArrowLeft, Clock, User, Phone } from 'react-feather';
import { TrackingMapView } from './TrackingMapView';
import './ReplacementTrackingScreen.css';

interface ReplacementTrackingScreenProps {
  request: ReplacementVehicleRequest;
  accident: Accident;
  onBack: () => void;
}

export const ReplacementTrackingScreen: React.FC<ReplacementTrackingScreenProps> = ({
  request,
  accident,
  onBack
}) => {
  const [vehiclePosition, setVehiclePosition] = useState(0); // 0 to 100 percent along route
  const [estimatedArrival, setEstimatedArrival] = useState('12 mins');
  const [distanceRemaining, setDistanceRemaining] = useState('4.2 km');

  // Simulate vehicle movement
  useEffect(() => {
    const interval = setInterval(() => {
      setVehiclePosition(prev => {
        if (prev >= 100) return 0; // Loop back for demo
        const newPos = prev + 0.5; // Move 0.5% every second
        
        // Update ETA and distance based on position
        const remainingPercent = 100 - newPos;
        const totalTime = 12; // 12 minutes total
        const remainingTime = Math.ceil((remainingPercent / 100) * totalTime);
        const totalDistance = 4.2; // 4.2 km total
        const remainingDistance = ((remainingPercent / 100) * totalDistance).toFixed(1);
        
        setEstimatedArrival(`${remainingTime} mins`);
        setDistanceRemaining(`${remainingDistance} km`);
        
        return newPos;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const driverInfo = {
    name: 'Michael Chen',
    phone: '+61 2 9123 4567',
    vehicle: 'Toyota Camry - White',
    license: 'ABC 123'
  };

  return (
    <div className="tracking-screen">
      {/* Header */}
      <div className="tracking-header">
        <button className="back-button" onClick={onBack}>
          <ArrowLeft size={20} />
        </button>
        <h1 className="tracking-title">Vehicle Tracking</h1>
        <div className="tracking-status">
          <div className="status-dot active"></div>
          <span>En Route</span>
        </div>
      </div>

      {/* ETA Summary */}
      <div className="eta-summary">
        <div className="eta-header">
          <h2>Your replacement vehicle is on the way</h2>
          <div className="eta-card">
            <Clock size={16} />
            <span className="eta-time">{estimatedArrival}</span>
            <span className="eta-label">ETA</span>
          </div>
        </div>
        <div className="distance-info">
          <span className="distance-remaining">{distanceRemaining}</span>
          <span className="distance-label">remaining</span>
        </div>
      </div>

      {/* Live Map Tracking */}
      <div className="map-container">
        <TrackingMapView 
          request={request}
          accident={accident}
          vehiclePosition={vehiclePosition}
        />
      </div>

      {/* Driver Info Card */}
      <div className="driver-card">
        <div className="driver-header">
          <div className="driver-icon">
            <User size={20} />
          </div>
          <div className="driver-details">
            <h3>{driverInfo.name}</h3>
            <span className="driver-vehicle">{driverInfo.vehicle}</span>
          </div>
          <button className="call-button">
            <Phone size={18} />
          </button>
        </div>
        <div className="driver-info">
          <div className="info-item">
            <span className="info-label">License</span>
            <span className="info-value">{driverInfo.license}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Phone</span>
            <span className="info-value">{driverInfo.phone}</span>
          </div>
        </div>
      </div>

      {/* Request Details */}
      <div className="request-details">
        <div className="request-header">
          <h3>Request Details</h3>
          <span className="request-id">#{request.id.slice(-6)}</span>
        </div>
        <div className="request-info">
          <div className="request-item">
            <span className="request-label">Customer</span>
            <span className="request-value">{request.leadInfo.fullName}</span>
          </div>
          <div className="request-item">
            <span className="request-label">Contact</span>
            <span className="request-value">{request.leadInfo.phone}</span>
          </div>
          <div className="request-item">
            <span className="request-label">Requested</span>
            <span className="request-value">{new Date(request.requestedAt).toLocaleTimeString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}; 